import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Button, OrgCreateForm, Pill, Skeleton } from '../components/primitives';
import { EmptyState } from '../components/primitives';
import { SettingsBackLink } from '../components/settings/SettingsBackLink';
import { useSettingsOrganizationsVM } from '../lib/vm/useSettingsOrganizationsVM';
import type { SignedInShellOutletContext } from '../components/shell/SignedInShell';
import './SettingsOrganizations.css';

/**
 * `/settings/organizations` — lists all orgs the signed-in user belongs to.
 * Provides a "+ Create new org" affordance (via OrgCreateForm) and per-row
 * "Switch" buttons for non-active orgs.
 *
 * Reuses the host shell's create+switch loop via outlet context — no second
 * VM instance.
 */
export function SettingsOrganizations() {
  const ctx = useOutletContext<SignedInShellOutletContext>();
  const list = useSettingsOrganizationsVM();
  const [createOpen, setCreateOpen] = useState(false);

  async function handleCreate(
    name: string
  ): Promise<{ ok: true } | { ok: false; error: string }> {
    const r = await ctx.createOrg(name);
    // On success the host shell fires the org switch + toast and navigates to
    // /dashboard, which unmounts this page — so do NOT touch local state or
    // refresh the list afterward (those writes would land on an unmounted
    // component). On failure, keep the form open so the inline error shows.
    return r;
  }

  return (
    <section
      className="set-orgs-page"
      data-testid="settings-organizations"
    >
      <SettingsBackLink />

      <header className="set-orgs-header">
        <div className="set-orgs-header-text">
          <h1 className="set-orgs-title">Organizations</h1>
          <p className="set-orgs-sub">
            All organizations you belong to. Switch context or create a new org.
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setCreateOpen((v) => !v)}
          data-testid="settings-orgs-create-open"
        >
          + Create new org
        </Button>
      </header>

      {createOpen ? (
        <div className="set-orgs-create-wrap">
          <OrgCreateForm
            variant="settings"
            testIdPrefix="settings-orgs-create-form"
            onCancel={() => setCreateOpen(false)}
            onSubmit={handleCreate}
            disabled={list.status === 'loading'}
          />
        </div>
      ) : null}

      {list.status === 'loading' ? (
        <div className="set-orgs-loading" data-testid="settings-orgs-loading">
          <Skeleton height={48} />
          <Skeleton height={48} />
          <Skeleton height={48} />
        </div>
      ) : null}

      {list.status === 'error' ? (
        <EmptyState data-testid="settings-orgs-error">
          {list.error ?? "Couldn't load your organizations. Try again."}
        </EmptyState>
      ) : null}

      {list.status === 'unauth' ? (
        <EmptyState data-testid="settings-orgs-unauth">
          Sign in to view your organizations.
        </EmptyState>
      ) : null}

      {list.status === 'ready' ? (
        <ul className="set-orgs-list" data-testid="settings-orgs-list">
          {list.rows.map((row) => {
            const isActive = row.org_id === ctx.activeOrgId;
            return (
              <li
                key={row.org_id}
                className="set-orgs-row"
                data-testid="settings-orgs-row"
                data-org-id={row.org_id}
              >
                <div className="set-orgs-row-info">
                  <span className="set-orgs-row-name">{row.name}</span>
                  <span className="set-orgs-row-meta">
                    <span className="set-orgs-row-role">
                      {row.role_label ?? '—'}
                    </span>
                    <span className="set-orgs-row-members">
                      {row.member_count}{' '}
                      {row.member_count === 1 ? 'member' : 'members'}
                    </span>
                  </span>
                </div>
                <div className="set-orgs-row-action">
                  {isActive ? (
                    <Pill
                      status="active"
                      data-testid="settings-orgs-row-active"
                    >
                      Currently active
                    </Pill>
                  ) : (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => ctx.switchOrg(row.org_id, row.name)}
                      data-testid="settings-orgs-row-switch"
                    >
                      Switch
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
